import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import RemoteBox from '../pages/RemoteBox';
import DenyAccess from '../pages/DenyAccess';
import { addLeadingVision } from '../utils/utils';
import { NavItem } from '../interfaces/userContext';

interface DynamicRoutesProps {
    navItems: NavItem[];
}

const DynamicRoutes: React.FC<DynamicRoutesProps> = ({ navItems }) => {
    // Extract the base paths from navItems to create root-level module paths
    const baseModules = new Set<string>();

    navItems.forEach(navItem => {
        const fullPath = addLeadingVision(navItem.path);
        // Get the first two segments: 'vision/moduleName'
        const segments = fullPath.split('/');
        if (segments.length >= 2) {
            const basePath = `${segments[0]}/${segments[1]}`;
            baseModules.add(basePath);
        }
    });

    console.log('Base modules extracted:', Array.from(baseModules));

    return (
        <Routes>
            {/* Home routes - handle both root and vision home */}
            <Route path="/" element={<RemoteBox path="vision/" />} />
            <Route path="/vision" element={<RemoteBox path="vision/" />} />
            <Route path="/vision/" element={<RemoteBox path="vision/" />} />

            {/* Auth deny route */}
            <Route path="/auth/deny" element={<DenyAccess />} />


            {/* Redirect old track-trace route to new track-trace/ambient route */}
            <Route 
                path="/vision/track-trace" 
                element={<Navigate to="/vision/track-trace/ambient" replace />} 
            />

            {/* First, add exact routes for all specified nav items */}
            {navItems.map((navItem) => {
                const resolvedPath = addLeadingVision(navItem.path);
                return (
                    <Route
                        key={`exact-${resolvedPath}`}
                        path={resolvedPath}
                        element={<RemoteBox path={resolvedPath} navItem={navItem} />}
                    />
                );
            })}

            {/* Then, add wildcard routes for each base module to support deeper paths */}
            {Array.from(baseModules).map(baseModule => {
                // Find the corresponding nav item for this base module
                const matchingNavItem = navItems.find(item => {
                    const fullPath = addLeadingVision(item.path);
                    return fullPath === baseModule || fullPath.startsWith(`${baseModule}/`);
                });

                // Create wildcard path: 'vision/beacon/*'
                const wildcardPath = `${baseModule}/*`;

                return (
                    <Route
                        key={`wildcard-${baseModule}`}
                        path={wildcardPath}
                        element={<RemoteBox path={baseModule} navItem={matchingNavItem} />}
                    />
                );
            })}

            {/* Fallback route with helpful diagnostics */}
            <Route
                path="*"
                element={
                    <Navigate
                        to={{
                            pathname: "/auth/deny",
                            search: `?scope=route&path=${encodeURIComponent(window.location.pathname)}`
                        }}
                        replace
                    />
                }
            />
        </Routes>
    );
};

export default DynamicRoutes;